import React from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";
import { MarketingHeader, MarketingFooter } from "@/components/MarketingChrome";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const plans = [
  {
    id: "starter",
    name: "Starter",
    price: "Rp 99.000",
    period: "/bulan",
    tagline: "Untuk seller kecil yang baru mulai",
    features: [
      "1.000 customer database",
      "QR Loyalty basic",
      "Cashback code basic",
      "3 campaign WhatsApp/bulan",
      "Basic dashboard",
      "Email support",
    ],
    cta: "Mulai Starter",
  },
  {
    id: "growth",
    name: "Growth",
    price: "Rp 249.000",
    period: "/bulan",
    tagline: "Untuk seller aktif yang ingin scale",
    features: [
      "10.000 customer database",
      "Unlimited QR campaign",
      "WhatsApp automation",
      "Repeat order reminder",
      "Campaign template library",
      "Cashback & referral",
      "Basic profit dashboard",
      "Priority support",
    ],
    cta: "Pilih Growth",
    highlight: true,
    badge: "Paling Populer",
  },
  {
    id: "pro",
    name: "Pro",
    price: "Rp 499.000",
    period: "/bulan",
    tagline: "Untuk brand lokal multichannel",
    features: [
      "50.000 customer database",
      "AI Marketing Assistant (Claude)",
      "Advanced segmentation",
      "Referral program lengkap",
      "Membership tier system",
      "SKU analytics",
      "Full profit dashboard",
      "Benchmark kategori",
    ],
    cta: "Pilih Pro",
  },
  {
    id: "business",
    name: "Business",
    price: "Custom",
    period: "",
    tagline: "Untuk brand besar & enterprise",
    features: [
      "Unlimited customer database",
      "API integration",
      "Dedicated success manager",
      "Advanced analytics",
      "Custom loyalty program",
      "White-label option",
      "SLA & priority infrastructure",
    ],
    cta: "Hubungi Sales",
  },
];

export default function Pricing() {
  return (
    <div className="min-h-screen bg-white">
      <MarketingHeader />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50 rounded-full px-3 py-1">
              <Sparkles className="w-3 h-3 mr-1.5" /> Harga transparan untuk UMKM
            </Badge>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 leading-tight">
              Harga yang tumbuh<br />
              <span className="text-gradient-emerald">bersama bisnis Anda.</span>
            </h1>
            <p className="mt-5 text-gray-600 text-base sm:text-lg leading-relaxed">
              Mulai gratis 14 hari. Upgrade kapan pun. Tanpa kontrak jangka panjang. Semua harga sudah termasuk hosting, update, dan support.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((p) => (
              <div
                key={p.id}
                data-testid={`plan-${p.id}`}
                className={`relative rounded-2xl border p-6 flex flex-col ${
                  p.highlight
                    ? "border-emerald-500 bg-gradient-to-b from-emerald-50/50 to-white shadow-lg lg:scale-105"
                    : "border-gray-200 bg-white"
                }`}
              >
                {p.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                    {p.badge}
                  </div>
                )}
                <div>
                  <h3 className="font-heading text-xl font-bold text-gray-900">{p.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{p.tagline}</p>
                  <div className="mt-5">
                    <span className="font-heading text-4xl font-bold text-gray-900">{p.price}</span>
                    <span className="text-gray-500 text-sm">{p.period}</span>
                  </div>
                </div>

                <ul className="mt-6 space-y-2.5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
                      <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link to={p.id === "business" ? "/signup?plan=business" : "/signup"} className="mt-6">
                  <Button
                    className={`w-full h-11 font-semibold ${
                      p.highlight
                        ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                        : "bg-gray-900 hover:bg-gray-800 text-white"
                    }`}
                    data-testid={`plan-cta-${p.id}`}
                  >
                    {p.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>

          <div className="mt-14 max-w-3xl mx-auto text-center bg-gray-50 border border-gray-200 rounded-xl p-6">
            <div className="font-heading font-semibold text-gray-900">Pertanyaan?</div>
            <p className="mt-2 text-sm text-gray-600">
              Semua plan bisa upgrade/downgrade kapan pun. Pembayaran bulanan via transfer bank atau QRIS. Tidak ada biaya tersembunyi.
            </p>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
